import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, color } from 'framer-motion';
import btc_icon from "../images/crypto/BTC.webp";
import eth_icon from "../images/crypto/ETH.png";
import xrp_icon from "../images/crypto/XRP.png";
import sol_icon from "../images/crypto/SOL.png";
import "../css/Live.css";

const TransactionItem = ({ transaction, config }) => {
  const shortenAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  return (
    <motion.div
      className="item"
      initial={{ opacity: 0, y: -50, scale: 0.8 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.8 }}
      transition={{ 
        type: "spring", 
        stiffness: 300, 
        damping: 24,
        duration: 0.6 
      }}
      layout
    >
        <div className='crypto'>
            <img src={config.icon}/>
            <span>{config.name}</span>
        </div>
        <div className='details'>
            <div className='f'>
                <span>From</span>
                <p>{shortenAddress(transaction.from)}</p>
                <span>To</span>
                <p>{shortenAddress(transaction.to)}</p>
            </div>
        </div>
        <div className='footer'>
            <span>{transaction.amount} {transaction.crypto}</span>
            <div className='tag'>Completed</div>
        </div>
    </motion.div>
  );
};

const Live = ({ btcAddress, ethAddress, xrpAddress, solAddress }) => {
  const [transactions, setTransactions] = useState([]);

  const cryptoConfig = {
    BTC: {
      icon: btc_icon,
      name: 'Bitcoin',
      color: '#f7931a',
      address: btcAddress || '1A1zP1eP5QGefi2DMPTfTL5SLmv7DivfNa',
      minAmount: 0.1,
      maxAmount: 3,
      decimals: 8
    },
    ETH: {
      icon: eth_icon,
      name: 'Ethereum',
      color: '#627eea',
      address: ethAddress || '0x742d35Cc6634C0532925a3b8D3Ac92b4c6b4b8b8',
      minAmount: 0.5,
      maxAmount: 50,
      decimals: 6
    },
    SOL: {
      icon: sol_icon,
      name: 'Solana',
      color: '#9945ff',
      address: solAddress || '11111111111111111111111111111112',
      minAmount: 10,
      maxAmount: 1000,
      decimals: 4
    },
    XRP: {
      icon: xrp_icon,
      name: 'Ripple',
      color: '#006097',
      address: xrpAddress || '1111111111111111111111111111122223',
      minAmount: 100,
      maxAmount: 15000,
      decimals: 2
    }
  };

  const generateRandomAddress = (crypto) => {
    switch (crypto) {
      case 'BTC':
        const btcChars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
        let btcResult = '1';
        for (let i = 0; i < 33; i++) {
          btcResult += btcChars.charAt(Math.floor(Math.random() * btcChars.length));
        }
        return btcResult;
      
      case 'ETH':
        const hexChars = '0123456789abcdef';
        let ethResult = '0x';
        for (let i = 0; i < 40; i++) {
          ethResult += hexChars.charAt(Math.floor(Math.random() * hexChars.length));
        }
        return ethResult;
      
      case 'SOL':
        const base58Chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
        let solResult = '';
        for (let i = 0; i < 44; i++) {
          solResult += base58Chars.charAt(Math.floor(Math.random() * base58Chars.length));
        }
        return solResult;
      
      case 'XRP':
        const xrpBase58Chars = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
        let xrpResult = '';
        for (let i = 0; i < 44; i++) {
          xrpResult += xrpBase58Chars.charAt(Math.floor(Math.random() * xrpBase58Chars.length));
        }
        return xrpResult;
      
      default:
        return 'unknown';
    }
  };

  const generateRandomAmount = (config) => {
    const amount = Math.random() * (config.maxAmount - config.minAmount) + config.minAmount;
    return amount.toFixed(config.decimals);
  };

  const createTransaction = () => {
    const cryptos = ['BTC', 'ETH', 'SOL', 'XRP'];
    const randomCrypto = cryptos[Math.floor(Math.random() * cryptos.length)];
    const config = cryptoConfig[randomCrypto];

    return {
      id: Date.now() + Math.random(),
      crypto: randomCrypto,
      from: config.address,
      to: generateRandomAddress(randomCrypto),
      amount: generateRandomAmount(config),
      timestamp: new Date()
    };
  };

  // Генерируем начальные 20 транзакций
  useEffect(() => {
    const initialTransactions = [];
    const now = new Date();
    
    for (let i = 0; i < 20; i++) {
      const transaction = createTransaction();
      transaction.timestamp = new Date(now.getTime() - (i * 60000));
      transaction.id = now.getTime() - i;
      initialTransactions.push(transaction);
    }
    
    setTransactions(initialTransactions);
  }, []);

  // Добавляем новые транзакции периодически
  useEffect(() => {
    const interval = setInterval(() => {
      const newTransaction = createTransaction();
      setTransactions(prev => [newTransaction, ...prev.slice(0, 19)]);
    }, Math.random() * 9000 + 6000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="box _live" id="live">
      <div className='container'>
        <motion.h2 
            className="feed-title"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            Live <span>transactions</span>
        </motion.h2>
        
        <motion.div 
            className="transactions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
        >
            <AnimatePresence mode="popLayout">
            {transactions.map((transaction) => (
                <TransactionItem
                key={transaction.id}
                transaction={transaction}
                config={cryptoConfig[transaction.crypto]}
                />
            ))}
            </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

export default Live;